import ServiceError from "./ServiceError"

async function execSubscriptionRequest(handler) {
  let response = null
  try {
    response = await handler()
  } catch (err) {
    console.error("Unable to request user subscriptions", err)
    throw new ServiceError(ServiceError.STATUS_CONNECTION_ERROR, err.message)
  }
  if (!response.ok) {
    throw new ServiceError(
      response.status,
      `Got no success status. Got status ${response.status}.`
    )
  }

  const data = await response.json()
  if (!data.subscriptionKeys) {
    console.error("Unexpected user subscription response", data)
    throw new ServiceError(
      ServiceError.STATUS_INVALID_DATA,
      `Invalid response data.`
    )
  }
  return data.subscriptionKeys
}

/**
 * Will load the array of category keys the user subscribed to.
 */
export async function getSubscriptionKeys() {
  return execSubscriptionRequest(() =>
    fetch(
      `${process.env.PERSONALISATION_HOST}/api/content/category/subscription`,
      {
        mode: "cors",
        credentials: "include",
      }
    )
  )
}

/**
 * Will add the key to the list of subscribed category keys
 * and return the array of category keys the user subscribed to.
 */
export async function addSubscriptionKey(subscriptionKey) {
  return execSubscriptionRequest(() =>
    fetch(
      `${
        process.env.PERSONALISATION_HOST
      }/api/content/category/subscription/${subscriptionKey}`,
      {
        method: "PUT",
        mode: "cors",
        credentials: "include",
      }
    )
  )
}

/**
 * Will delete the key from the list of subscribed category keys
 * and return the array of category keys the user subscribed to.
 */
export async function deleteSubscriptionKey(subscriptionKey) {
  return execSubscriptionRequest(() =>
    fetch(
      `${
        process.env.PERSONALISATION_HOST
      }/api/content/category/subscription/${subscriptionKey}`,
      {
        method: "DELETE",
        mode: "cors",
        credentials: "include",
      }
    )
  )
}

/**
 * Will subscribe the user to all known category keys
 * and return the array of these category keys.
 */
export async function addAllSubscriptionKeys() {
  return execSubscriptionRequest(() =>
    fetch(
      `${process.env.PERSONALISATION_HOST}/api/content/category/subscription`,
      {
        method: "PUT",
        mode: "cors",
        credentials: "include",
      }
    )
  )
}

/**
 * Will delete all the the subscribed category keys
 * and return the array of category keys the user subscribed to
 * which should be an empty list.
 */
export async function deleteAllSubscriptionKeys() {
  return execSubscriptionRequest(() =>
    fetch(
      `${process.env.PERSONALISATION_HOST}/api/content/category/subscription`,
      {
        method: "DELETE",
        mode: "cors",
        credentials: "include",
      }
    )
  )
}

export const SubscriptionStatus = {
  subscribedNone: "subscribedNone",
  subscribedAll: "subscribedAll",
  subscribedAllButOne: "subscribedAllButOne",
  subscribedSome: "subscribedSome",
}

/**
 * Will check the users subscribed keys against the given possibleKeys
 * and return the SubscriptionStatus for the possibleKeys.
 * @param possibleKeys An array of subscription keys.
 * @return {Promise<{unsubscribedCategoriesCount: number, status: string}
 */
export async function getSubscriptionStatus(possibleKeys) {
  const subscribedKeys = await getSubscriptionKeys()
  const notSubscribed = possibleKeys.filter(
    key => !subscribedKeys.includes(key)
  )
  if (notSubscribed.length === 0) {
    return {
      status: SubscriptionStatus.subscribedAll,
      unsubscribedCategoriesCount: 0,
    }
  }
  if (notSubscribed.length === 1) {
    return {
      status: SubscriptionStatus.subscribedAllButOne,
      unsubscribedCategoriesCount: 1,
    }
  }
  if (notSubscribed.length === possibleKeys.length) {
    return {
      status: SubscriptionStatus.subscribedNone,
      unsubscribedCategoriesCount: notSubscribed.length,
    }
  }
  return {
    status: SubscriptionStatus.subscribedSome,
    unsubscribedCategoriesCount: notSubscribed.length,
  }
}
